const BATCH_ID_KEY = 'x-batch-id'

/**
 * 课程类型常量
 */
export const COURSE_TYPES = {
  WORD: 'word',
  RECITE: 'recite',
  READING: 'reading'
}

/**
 * 计算字符串的 UTF-8 字节长度
 * @param {String} str
 * @returns {Number}
 */
function getByteLength (str) {
  return new TextEncoder().encode(str).length
}

/**
 * 按字节截断字符串（不破坏多字节字符）
 * @param {String} str
 * @param {Number} maxBytes
 * @returns {String}
 */
function truncateByBytes (str, maxBytes) {
  if (getByteLength(str) <= maxBytes) {
    return str
  }

  let truncated = str
  while (getByteLength(truncated) > maxBytes && truncated.length > 0) {
    truncated = truncated.slice(0, -1)
  }

  return truncated
}

const BATCH_ID_TOKEN_LENGTH = 16
const BATCH_ID_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789'

const getTodayDateString = () => {
  const now = new Date()
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  return `${yyyy}${mm}${dd}`
}

const generateRandomToken = (length = BATCH_ID_TOKEN_LENGTH) => {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(length)
    crypto.getRandomValues(bytes)
    return Array.from(bytes)
      .map((byte) => BATCH_ID_CHARS[byte % BATCH_ID_CHARS.length])
      .join('')
  }

  let token = ''
  for (let i = 0; i < length; i += 1) {
    token += BATCH_ID_CHARS.charAt(Math.floor(Math.random() * BATCH_ID_CHARS.length))
  }
  return token
}

/**
 * 生成 x-batch-id: YYYYMMDD-<16位随机串>
 * @returns {String}
 */
export const generateBatchId = () => {
  let batchId = `${getTodayDateString()}-${generateRandomToken()}`

  if (getByteLength(batchId) > 64) {
    batchId = truncateByBytes(batchId, 64)
  }

  return batchId
}

/**
 * 从 x-batch-id 中解析信息
 * Format: userId_courseType_recordId (新格式) 或 userId_courseType_recordId_timestamp (旧格式兼容)
 * @param {String} batchId - x-batch-id 字符串
 * @returns {Object|null} { userId, courseType, recordId, timestamp }
 */
export const parseBatchId = (batchId) => {
  if (!batchId) return null
  const match = /^(\d{8})-([a-z0-9]{16})$/i.exec(batchId)
  if (match) {
    return { date: match[1], token: match[2] }
  }
  const parts = batchId.split('_')
  if (parts.length < 3) return null
  return {
    userId: parts[0],
    courseType: parts[1],
    recordId: parts[2],
    timestamp: parts.length >= 4 ? parseInt(parts[3], 10) || null : null
  }
}

/**
 * 生成并缓存 x-batch-id
 * @returns {String} 生成的 batchId
 */
export const createBatchId = () => {
  const newBatchId = generateBatchId()
  return setBatchId(newBatchId)
}

/**
 * 获取缓存的 x-batch-id
 * @returns {String|null} 缓存的 batchId
 */
export const getCachedBatchId = () => {
  const cached = localStorage.getItem(BATCH_ID_KEY)
  const todayPrefix = `${getTodayDateString()}-`
  if (cached && cached.startsWith(todayPrefix)) {
    return cached
  }
  return createBatchId()
}

/**
 * 设置 x-batch-id
 * @param {String} batchId
 * @returns {String|null}
 */
export const setBatchId = (batchId) => {
  if (!batchId) return null
  localStorage.setItem(BATCH_ID_KEY, batchId)
  return batchId
}

/**
 * 清除 x-batch-id
 */
export const clearBatchId = () => {
  localStorage.removeItem(BATCH_ID_KEY)
}
